// services/api.ts
import axios, { type InternalAxiosRequestConfig, type RawAxiosRequestHeaders } from 'axios';
import { getAccessToken, getMsalInstance } from './msal';
import { env } from '../env';

const api = axios.create();

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    config.baseURL = env.API_URL || '/';

    const token = await getAccessToken([env.CLIENT_SCOPE]);
    if (token) {
      const next: RawAxiosRequestHeaders = {
        ...(config.headers as RawAxiosRequestHeaders | undefined),
        Authorization: `Bearer ${token}`,
      };
      // @ts-expect-error axios accepts plain object headers
      config.headers = next;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const original: any = error.config;
    const status = error?.response?.status;

    // network errors, etc.
    if (!original) return Promise.reject(error);

    if (status === 401 && !original.__msalRetried) {
      original.__msalRetried = true;

      // try a forced silent refresh once
      const token = await getAccessToken([env.CLIENT_SCOPE], true);
      if (token) {
        original.headers = {
          ...(original.headers || {}),
          Authorization: `Bearer ${token}`,
        };
        return api(original);
      }

      if (!original.__msalRedirected) {
        original.__msalRedirected = true;
        const msal = await getMsalInstance();

        msal.loginRedirect({ scopes: [env.CLIENT_SCOPE] });
        return new Promise(() => {
          /* never resolves because we navigated */
        });
      }
    }

    return Promise.reject(error);
  },
);

export default api;
